import { ReactElement, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { SimpleShape } from '@scene'
import { filter, find } from 'lodash-es'
import { Box3, Vector3 } from 'three'
import { useTheme } from '@mui/material'
import { useCameraEvent } from '@editor/hooks'
import {
  DraggableRectangle,
  HighlightableRectangle,
  RectangleXYZ,
  getFlatShapeNormal,
} from '@modugen/ui-toolkit'
import { useControlStore, useModelStore, useSceneStore } from '@editorStores'

interface Props {
  wallGuid: string

  selectedOpenings?: string[]
  onClickOpening?: (guid: string) => void
}

const OrthographicWall = ({
  wallGuid,
  selectedOpenings = [],
  onClickOpening,
}: Props): ReactElement => {
  const { scenePalette } = useTheme()
  const { walls } = useModelStore(state => state.model)
  const guidToColor = useSceneStore(state => state.guidToColor)

  const [hoveredOpening, setHoveredOpening] = useState<null | string>(null)

  const setOrthographicDrawMode = useControlStore(state => state.setOrthographicDrawMode)

  // MEMOS

  const wall = useMemo(() => find(walls, { guid: wallGuid }), [wallGuid, walls]) as ShapeObject

  const wallNormal = useMemo(() => getFlatShapeNormal(wall.shape.points as RectangleXYZ), [wall])

  const openingsInSelectedWall = useMemo(
    () =>
      wall &&
      wall.openings &&
      Object.fromEntries(
        wall.openings.map(opening => {
          return [
            opening.guid,
            {
              is_local: opening.is_local,
              points: opening.shape.points,
            },
          ]
        }),
      ),
    [wallGuid, wall],
  )

  const wallOpenings = filter(wall.openings, opening => !opening.is_local).map(
    opening => opening.shape.points,
  )

  const wallShape = wall?.shape.points as RectangleXYZ

  // EFFECTS

  // Only the draw mode is announced here; SceneControlled owns the camera and model visibility and
  // derives both from it (plus the storey). Setting them here too meant this component's cleanup
  // dropped the camera out of 2D even when a floorplan storey was still active.
  useEffect(() => {
    setOrthographicDrawMode(true)

    return () => setOrthographicDrawMode(false)
  }, [setOrthographicDrawMode])

  const [config, setConfig] = useState<{
    center: [number, number, number]
    length: number
    width: number
    normal: [number, number, number]
  } | null>(null)

  useLayoutEffect(() => {
    if (config === null) {
      const box = new Box3()
      wallShape.forEach(point => {
        box.expandByPoint(new Vector3(point.x, point.y, point.z))
      })
      box.expandByScalar(0.5)

      const normal = getFlatShapeNormal(wallShape)

      setConfig({
        center: box.getCenter(new Vector3()).toArray(),
        length: wallShape[0].distanceTo(wallShape[1]),
        width: wallShape[1].distanceTo(wallShape[2]),
        normal: normal.v.negate().toArray(),
      })
    }
  }, [wallShape, config])

  const cameraEvent = useMemo(() => {
    if (config === null) return null

    const event: FitToRectOrthographicCameraEvent = {
      type: 'fitToRectOrthographic',
      rectCenter: config.center,
      rectWidth: config.width,
      rectLength: config.length,
      rectNormal: config.normal,
      cameraType: 'orthographic',
      priority: 2,
    }

    return event
  }, [config])

  useCameraEvent(cameraEvent)

  return (
    <>
      <SimpleShape
        points={wall?.shape.points}
        color={scenePalette.elements3d.walls}
        openings={wallOpenings}
        name="wall"
        userData={{
          type: 'wall',
        }}
        thickness={0}
      />

      {openingsInSelectedWall &&
        Object.entries(openingsInSelectedWall).map(([openingGuid, opening]) => (
          <>
            {opening.is_local ? (
              <DraggableRectangle
                key={`${openingGuid}-local`}
                color="blue"
                points={opening.points as RectangleXYZ}
                rectangleProps={{
                  offset: {
                    direction: wallNormal,
                    distance: 0.5,
                  },
                }}
              />
            ) : (
              <HighlightableRectangle
                key={openingGuid}
                color={(guidToColor[openingGuid] || scenePalette.elements3d.openings) as string}
                points={opening.points as RectangleXYZ}
                hoverable
                onHover={hovered => setHoveredOpening(hovered ? openingGuid : null)}
                outlines={hoveredOpening === openingGuid || selectedOpenings.includes(openingGuid)}
                onClick={() => onClickOpening?.(openingGuid)}
                name="opening"
                boundingColor={
                  (guidToColor[openingGuid] || scenePalette.elements3d.openings) as string
                }
                boundingWidth={2}
                cursor="pointer"
              />
            )}
          </>
        ))}
    </>
  )
}

export default OrthographicWall
